/* eslint-disable consistent-return,max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import 'moment/locale/es';
// @material-ui/icons
import Check from '@material-ui/icons/Check';
import SearchIco from '@material-ui/icons/Search';
// @material-ui/core components
import FilterStyles from 'assets/jss/AdsFilter';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// global variables
import {
  processorRulesActions,
  processorRulesStatus,
  processorRulesType,
} from 'config';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import CustomInput from 'components/UI/CustomInput/CustomInput';
// our components
import SectionDivider from 'components/common/SectionDivider/SectionDivider';

/**
 * Variable to define default selected status
 */
const defaultSelectedStatus = [
  processorRulesStatus.ENABLED,
  processorRulesStatus.DISABLED,
  processorRulesStatus.DELETED,
];

/**
 * Variable to define default selected ruleType
 */
const defaultSelectedRuleTypes = [
  processorRulesType.REVIEW,
  processorRulesType.BLOCK,
  processorRulesType.ALLOW,
];

/**
 * Variable to define default selected actions
 */
const defaultSelectedActions = [
  processorRulesActions.COMMON,
  processorRulesActions.UPDATE,
  processorRulesActions.INSERT,
];

/**
 * @class
 * @extends React.Component
 */
class ProcessorRuleFilter extends React.Component {
  constructor(props) {
    super(props);
    const { appliedFilter } = props;
    this.state = {
      selectedStatus:
        (appliedFilter && appliedFilter.status) || defaultSelectedStatus,
      selectedActions:
        (appliedFilter && appliedFilter.actions) || defaultSelectedActions,
      selectedRuleType:
        (appliedFilter && appliedFilter.ruleType) || defaultSelectedRuleTypes,
      textToSearch: (appliedFilter && appliedFilter.nameOrGroup) || undefined,
      localFilter: appliedFilter || null,
    };
  }

  /**
   * @description
   * Change filters.
   *
   * @param {Object} changedFilter - Changed filters values.
   * @returns {*} - No returns value.
   */
  handleChangeFilter = changedFilter => {
    const { localFilter } = this.state;
    const { filter } = this.props;
    const filters = { ...localFilter, ...changedFilter };
    this.setState({ localFilter: filters });
    filter(filters);
  };

  /**
   * @description
   * Update new selection filter type.
   *
   * @param {string} filter - Filter type to update.
   * @param {*} callback - Callback waiting for setState.
   * @returns {*} - An update function.
   */
  updateSelectedCheckboxes = (filter, callback) => {
    const { selectedStatus, selectedRuleType, selectedActions } = this.state;
    switch (filter) {
      case 'status':
        return this.handleChangeFilter(
          selectedStatus.length === 0 || selectedStatus.length === 3
            ? { status: undefined }
            : { status: selectedStatus },
          callback,
        );
      case 'ruleType':
        return this.handleChangeFilter(
          selectedRuleType.length === 0 || selectedRuleType.length === 3
            ? { ruleType: undefined }
            : { ruleType: selectedRuleType },
          callback,
        );
      case 'action':
        return this.handleChangeFilter(
          selectedActions.length === 0 || selectedActions.length === 3
            ? { actions: undefined }
            : { actions: selectedActions },
          callback,
        );
      default:
    }
  };

  /**
   * @description
   * Handle select status checkboxes.
   *
   * @param {string} checkLabel - Status selected.
   * @param {*} callback - Callback waiting for setState.
   * @returns {undefined} No return.
   */
  handleStatusCheck = (checkLabel, callback = null) => {
    const { selectedStatus } = this.state;
    if (selectedStatus.indexOf(checkLabel) !== -1) {
      const newSelected = selectedStatus.filter(
        status => status !== checkLabel,
      );
      this.setState({ selectedStatus: newSelected }, () =>
        this.updateSelectedCheckboxes('status', callback),
      );
    } else {
      this.setState(
        {
          selectedStatus: [...selectedStatus, checkLabel],
        },
        () => this.updateSelectedCheckboxes('status', callback),
      );
    }
  };

  /**
   * @description
   * Handle select actions checkboxes.
   *
   * @param {string} checkLabel - Action selected.
   * @param {*} callback - Callback waiting for setState.
   * @returns {undefined} No return.
   */
  handleActionsCheck = (checkLabel, callback = null) => {
    const { selectedActions } = this.state;
    if (selectedActions.indexOf(checkLabel) !== -1) {
      const newSelected = selectedActions.filter(
        status => status !== checkLabel,
      );
      this.setState({ selectedActions: newSelected }, () =>
        this.updateSelectedCheckboxes('action', callback),
      );
    } else {
      this.setState(
        {
          selectedActions: [...selectedActions, checkLabel],
        },
        () => this.updateSelectedCheckboxes('action', callback),
      );
    }
  };

  /**
   * @description
   * Handle select ruleType checkboxes.
   *
   * @param {string} checkLabel - RuleType selected.
   * @param {*} callback - Callback waiting for setState.
   * @returns {undefined} No return.
   */
  handleRuleTypeCheck = (checkLabel, callback = null) => {
    const { selectedRuleType } = this.state;
    if (selectedRuleType.indexOf(checkLabel) !== -1) {
      const newSelected = selectedRuleType.filter(
        status => status !== checkLabel,
      );
      this.setState({ selectedRuleType: newSelected }, () =>
        this.updateSelectedCheckboxes('ruleType', callback),
      );
    } else {
      this.setState(
        {
          selectedRuleType: [...selectedRuleType, checkLabel],
        },
        () => this.updateSelectedCheckboxes('ruleType', callback),
      );
    }
  };

  /**
   * @description
   * Handle search change.
   *
   * @param {Object} changedValue - Changed search value.
   * @returns {undefined} No return.
   */
  handleSearchChange = changedValue => {
    this.setState({
      textToSearch: changedValue,
    });
  };

  /**
   * @description
   * Handle search.
   * @returns {undefined} No return.
   */
  handleSearch = () => {
    const { textToSearch } = this.state;

    this.handleChangeFilter({
      nameOrGroup: textToSearch || undefined,
    });
  };

  /**
   * @description
   * Render full filter's box.
   * @returns {*} - AdFilter's box.
   */
  render() {
    const { classes, restartFilters, t } = this.props;
    const {
      selectedStatus,
      selectedActions,
      selectedRuleType,
      textToSearch,
    } = this.state;
    return (
      <div className={classes.expandFilters} style={{ marginBottom: 20 }}>
        <GridContainer>
          <GridItem xs={12}>
            <div style={{ padding: '0px 10px 10px 10px' }}>
              <GridContainer>
                <GridItem xs={12} md={4}>
                  <GridItem>
                    <CustomInput
                      data-cy="processorRuleNameOrGroupInput"
                      id="nameOrGroup"
                      formControlProps={{
                        className: `${classes.top} ${classes.search}`,
                      }}
                      inputProps={{
                        placeholder: t(
                          'processor_rule.filters.name_or_group_placeholder',
                        ),
                        inputProps: {
                          'aria-label': 'Search',
                          className: classes.searchInput,
                        },
                        endAdornment: (
                          <Button
                            id="searchByNameOrGroupButton"
                            justIcon
                            simple
                            onClick={this.handleSearch}
                            color="primary"
                            className={classes.searchBtn}
                          >
                            <SearchIco className={classes.inputAdornmentIcon} />
                          </Button>
                        ),
                        onChange: e => {
                          const { value } = e.target;
                          this.handleSearchChange(value);
                        },
                        onKeyPress: e => {
                          if (e.key === 'Enter') {
                            this.handleSearch();
                          }
                        },
                        value: textToSearch !== undefined ? textToSearch : '',
                        type: 'text',
                      }}
                    />
                  </GridItem>
                </GridItem>
              </GridContainer>
              <GridContainer style={{ marginTop: '15px' }}>
                <GridItem xs={12} md={4}>
                  <GridItem>
                    <SectionDivider
                      label={t('processor_rule.filters.section.rule_type')}
                    >
                      <GridItem>
                        <FormControlLabel
                          control={
                            <Checkbox
                              className={classes.checked}
                              onClick={() =>
                                this.handleRuleTypeCheck(
                                  processorRulesType.ALLOW,
                                )
                              }
                              checked={
                                selectedRuleType.indexOf(
                                  processorRulesType.ALLOW,
                                ) !== -1
                              }
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                            />
                          }
                          id="ruleTypeAllowCheckbox"
                          label={processorRulesType.ALLOW}
                          style={{ width: '100%' }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              className={classes.checked}
                              onClick={() =>
                                this.handleRuleTypeCheck(
                                  processorRulesType.BLOCK,
                                )
                              }
                              checked={
                                selectedRuleType.indexOf(
                                  processorRulesType.BLOCK,
                                ) !== -1
                              }
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                            />
                          }
                          id="ruleTypeBlockCheckbox"
                          label={processorRulesType.BLOCK}
                          style={{ width: '100%' }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              className={classes.checked}
                              onClick={() =>
                                this.handleRuleTypeCheck(
                                  processorRulesType.REVIEW,
                                )
                              }
                              checked={
                                selectedRuleType.indexOf(
                                  processorRulesType.REVIEW,
                                ) !== -1
                              }
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                            />
                          }
                          id="ruleTypeReviewCheckbox"
                          label={processorRulesType.REVIEW}
                          style={{ width: '100%' }}
                        />
                      </GridItem>
                    </SectionDivider>
                  </GridItem>
                </GridItem>
                <GridItem xs={12} md={4}>
                  <GridItem>
                    <SectionDivider
                      label={t('processor_rule.filters.section.action')}
                    >
                      <GridItem>
                        <FormControlLabel
                          control={
                            <Checkbox
                              className={classes.checked}
                              onClick={() =>
                                this.handleActionsCheck(
                                  processorRulesActions.INSERT,
                                )
                              }
                              checked={
                                selectedActions.indexOf(
                                  processorRulesActions.INSERT,
                                ) !== -1
                              }
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                            />
                          }
                          id="actionInsertCheckbox"
                          label={processorRulesActions.INSERT}
                          style={{ width: '100%' }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              className={classes.checked}
                              onClick={() =>
                                this.handleActionsCheck(
                                  processorRulesActions.UPDATE,
                                )
                              }
                              checked={
                                selectedActions.indexOf(
                                  processorRulesActions.UPDATE,
                                ) !== -1
                              }
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                            />
                          }
                          id="actionUpdateCheckbox"
                          label={processorRulesActions.UPDATE}
                          style={{ width: '100%' }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              className={classes.checked}
                              onClick={() =>
                                this.handleActionsCheck(
                                  processorRulesActions.COMMON,
                                )
                              }
                              checked={
                                selectedActions.indexOf(
                                  processorRulesActions.COMMON,
                                ) !== -1
                              }
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                            />
                          }
                          id="actionCommonCheckbox"
                          label={processorRulesActions.COMMON}
                          style={{ width: '100%' }}
                        />
                      </GridItem>
                    </SectionDivider>
                  </GridItem>
                </GridItem>
                <GridItem xs={12} md={4}>
                  <GridItem>
                    <SectionDivider
                      label={t('processor_rule.filters.section.status')}
                    >
                      <GridItem>
                        <FormControlLabel
                          control={
                            <Checkbox
                              className={classes.checked}
                              onClick={() =>
                                this.handleStatusCheck(
                                  processorRulesStatus.ENABLED,
                                )
                              }
                              checked={
                                selectedStatus.indexOf(
                                  processorRulesStatus.ENABLED,
                                ) !== -1
                              }
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                            />
                          }
                          id="statusEnabledCheckbox"
                          label={processorRulesStatus.ENABLED}
                          style={{ width: '100%' }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              className={classes.checked}
                              onClick={() =>
                                this.handleStatusCheck(
                                  processorRulesStatus.DISABLED,
                                )
                              }
                              checked={
                                selectedStatus.indexOf(
                                  processorRulesStatus.DISABLED,
                                ) !== -1
                              }
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                            />
                          }
                          id="statusDisabledCheckbox"
                          label={processorRulesStatus.DISABLED}
                          style={{ width: '100%' }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              className={classes.checked}
                              onClick={() =>
                                this.handleStatusCheck(
                                  processorRulesStatus.DELETED,
                                )
                              }
                              checked={
                                selectedStatus.indexOf(
                                  processorRulesStatus.DELETED,
                                ) !== -1
                              }
                              checkedIcon={
                                <Check className={classes.checkedIcon} />
                              }
                              icon={<Check className={classes.uncheckedIcon} />}
                            />
                          }
                          id="statusDeletedCheckbox"
                          label={processorRulesStatus.DELETED}
                          style={{ width: '100%' }}
                        />
                      </GridItem>
                    </SectionDivider>
                  </GridItem>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <GridContainer justify="flex-end">
              <GridItem>
                <Button
                  color="danger"
                  simple
                  className={classes.buttons}
                  onClick={() => {
                    this.setState({
                      selectedStatus: defaultSelectedStatus,
                      selectedActions: defaultSelectedActions,
                      selectedRuleType: defaultSelectedRuleTypes,
                      textToSearch: undefined,
                      localFilter: {},
                    });
                    restartFilters();
                  }}
                  id="restartFilters"
                  key="restartFilters"
                >
                  {t('ad.filters.clean')}
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

/**
 * PaidAdFilter PropTypes
 * @param classes - styles received from withStyles
 * @param appliedFilter - prop to know applied filters
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with string translations
 * @callback filter - callback called when user clicks filter button
 * @callback restartFilters - callback called when user restart filters button
 */

ProcessorRuleFilter.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withStyles(FilterStyles)(ProcessorRuleFilter));
